import React from 'react';
import { LangBlock } from '../../global';
import { ADMIN_PAGES, AdminPage } from './admin.utils';

export { AdminButton } from './admin.buttons';

type AdminSectionProps = {
    title?: string;
    subtitle?: string;
    actions?: React.ReactNode;
    children: React.ReactNode;
};

export const AdminSection = ({ title, subtitle, actions, children }: AdminSectionProps) => (
    <div className="admin-section">
        {(title || subtitle || actions) && (
            <div className="admin-section__head">
                <div>
                    {title ? <h2 className="admin-section__title">{title}</h2> : null}
                    {subtitle ? <p className="admin-section__subtitle">{subtitle}</p> : null}
                </div>
                {actions ? <div className="admin-section__actions">{actions}</div> : null}
            </div>
        )}
        <div className="admin-section__body">{children}</div>
    </div>
);

export const AdminPageWrap = ({ children }: { children: React.ReactNode }) => (
    <div className="admin-page">{children}</div>
);

type AdminNavProps = {
    activePage: AdminPage;
    onSelect: (page: AdminPage) => void;
};

export const AdminNav = ({ activePage, onSelect }: AdminNavProps) => {
    const [, , LangString] = LangBlock();

    return (
        <nav className="admin-nav">
            {ADMIN_PAGES.map((page) => (
                <button
                    key={page.key}
                    type="button"
                    className={`admin-nav__item${activePage === page.key ? ' admin-nav__item--active' : ''}`}
                    onClick={() => onSelect(page.key)}
                >
                    {LangString(page.label)}
                </button>
            ))}
        </nav>
    );
};

type AdminBadgeProps = {
    tone?: 'ok' | 'warn' | 'danger' | 'neutral';
    children: React.ReactNode;
};

export const AdminBadge = ({ tone = 'neutral', children }: AdminBadgeProps) => (
    <span className={`admin-badge admin-badge--${tone}`}>{children}</span>
);

export const AdminCard = ({
    title,
    children,
    className = '',
}: {
    title?: string;
    children: React.ReactNode;
    className?: string;
}) => (
    <div className={`admin-card ${className}`.trim()}>
        {title ? <div className="admin-card__title">{title}</div> : null}
        <div className="admin-card__body">{children}</div>
    </div>
);

export const AdminGrid = ({
    columns = 3,
    children,
}: {
    columns?: 2 | 3 | 4;
    children: React.ReactNode;
}) => <div className={`admin-grid admin-grid--${columns}`}>{children}</div>;

export const AdminStatCard = ({
    label,
    value,
    tone = 'neutral',
    action,
}: {
    label: string;
    value: React.ReactNode;
    tone?: 'ok' | 'warn' | 'danger' | 'neutral';
    action?: React.ReactNode;
}) => (
    <div className={`admin-stat-card admin-stat-card--${tone}`}>
        <div className="admin-stat-card__label">{label}</div>
        <div className="admin-stat-card__value">{value}</div>
        {action ? <div className="admin-stat-card__action">{action}</div> : null}
    </div>
);

export function AdminLangTabs<T extends string>({
    langs,
    active,
    onSelect,
}: {
    langs: readonly T[];
    active: T;
    onSelect: (lang: T) => void;
}) {
    return (
    <div className="admin-lang-tabs">
        {langs.map((lang) => (
            <button
                key={lang}
                type="button"
                className={`admin-lang-tabs__item${active === lang ? ' admin-lang-tabs__item--active' : ''}`}
                onClick={() => onSelect(lang)}
            >
                {lang.toUpperCase()}
            </button>
        ))}
    </div>
    );
}

export const AdminFormGrid = ({ children }: { children: React.ReactNode }) => (
    <div className="admin-form-grid">{children}</div>
);

export const AdminFormField = ({
    label,
    children,
    wide,
}: {
    label?: string;
    children: React.ReactNode;
    wide?: boolean;
}) => (
    <div className={`admin-form-field${wide ? ' admin-form-field--wide' : ''}`}>
        {label ? <label className="admin-form-field__label">{label}</label> : null}
        <div className="admin-form-field__control">{children}</div>
    </div>
);

export const AdminPreview = ({ html }: { html: string }) => (
    <div className="admin-preview" dangerouslySetInnerHTML={{ __html: html }} />
);

export const AdminEmpty = ({ message }: { message: string }) => (
    <div className="admin-empty">{message}</div>
);
